import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApplicationModel, ApplicationPaymentModel } from '@/models/application.model'
import { admissionsService } from '@/services/admissionsService'
import { DocumentViewer } from '@/components/app/applications/DocumentViewer'
import { ApplicationStatusModal } from '@/components/app/applications/ApplicationStatusModal'
import { ApplicationProgrammeModal } from '@/components/app/applications/ApplicationProgrammeModal'
import { ApplicationPaymentVerificationModal } from '@/components/app/applications/ApplicationPaymentVerificationModal'
import { ChevronLeftCircle} from 'lucide-react'
import { programmesService } from '@/services/programmesService'
const primary = '#7b2a40'

const formatCurrency = (amount: number | null, currency?: string | null): string => {
    if (amount === null || Number.isNaN(amount)) return '—'
    return `${currency ?? 'NGN'} ${amount.toLocaleString()}`
}

const formatDate = (value?: string | null): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return value
    return parsed.toLocaleString()
}

const getPaymentStatusClass = (status?: string | null) => {
    const normalized = status?.toLowerCase()
    if (normalized === 'confirmed') return 'bg-green-100 text-green-700'
    if (normalized === 'rejected') return 'bg-red-100 text-red-700'
    return 'bg-yellow-100 text-yellow-800'
}

const ApplicationViewPage: React.FC = () => {
    const { applicationId: id } = useParams()
    const [isModalOpen, setModalOpen] = useState(false)
    const [isProgrammeModalOpen, setProgrammeModalOpen] = useState(false)
    const [isVerifyModalOpen, setVerifyModalOpen] = useState(false)
    const [selectedPayment, setSelectedPayment] = useState<ApplicationPaymentModel | null>(null)
    const navigate = useNavigate()

    const { data: app, isLoading, error } = useQuery<ApplicationModel>({
        queryKey: ['application', id],
        queryFn: () => admissionsService.getApplicationById(id!),
        enabled: !!id,
    })
    const programmeId = app?.programme_id ?? ''
    const { data: programme } = useQuery({
        queryKey: ['programme', programmeId],
        queryFn: () => programmesService.getProgramme(programmeId, { requireAuth: true }),
        enabled: Boolean(programmeId),
        staleTime: 1000 * 60 * 5,
    })

    const paymentProgress = useMemo(() => {
        if (!app) return null
        return {
            percent: app.paymentPercentComplete,
            total: app.totalDueAmount,
            paid: app.totalPaidAmount,
            currency: app.amount_currency ?? app.payments?.[0]?.currency ?? 'NGN',
            nextDue: app.nextInstallmentDueDate,
            installments: app.installments ?? [],
            paidInstallments: app.paidInstallments,
            totalInstallments: app.totalInstallments,
        }
    }, [app])

    
    const payments = useMemo(
        () => (app?.payments ?? []).slice().sort(
            (a, b) =>
                new Date(b.created_at ?? '').getTime() -
                new Date(a.created_at ?? '').getTime(),
        ),
        [app?.payments],
    )

    const handleVerifyClick = (payment: ApplicationPaymentModel) => {
        setSelectedPayment(payment)
        setVerifyModalOpen(true)
    }

    const handleCloseVerification = () => {
        setVerifyModalOpen(false)
        setSelectedPayment(null)
    }

    if (isLoading)
        return <div className="p-10 text-center text-gray-500">Loading application…</div>

    if (error || !app)
        return <div className="p-10 text-center text-red-600">Unable to load application</div>


    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className='inline-flex items-center gap-3'>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <ChevronLeftCircle className="h-6 w-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {app.first_name} {app.last_name}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setProgrammeModalOpen(true)}
                        className="px-4 py-2 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: primary }}
                    >
                        Update Programme
                    </button>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="px-4 py-2 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: primary }}
                    >
                        Update Status
                    </button>
                </div>
            </div>

            {/* Info Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                <h2 className="text-lg font-semibold mb-4 text-gray-900">Application Information</h2>

                <div className="grid md:grid-cols-2 gap-5 text-sm">
                    <div>
                        <p className="text-gray-500">Application ID</p>
                        <p className="font-medium">{app.app_id}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">Programme</p>
                        <p className="font-medium">
                            {programme?.title ?? app.programme_id ?? '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-500">Email</p>
                        <p className="font-medium">{app.email}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">Phone</p>
                        <p className="font-medium">{app.phone}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">Nationality</p>
                        <p className="font-medium">{app.nationality}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">Status</p>
                        <span
                            className="text-xs px-2.5 py-1 rounded-full border font-medium"
                            style={{ borderColor: primary, color: primary }}
                        >
                            {app.status}
                        </span>
                    </div>
                    <div>
                        <p className="text-gray-500">Amount Due</p>
                        <p className="font-medium">
                            {formatCurrency(app.totalDueAmount ?? null, app.amount_currency)}
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-500">Amount Paid</p>
                        <p className="font-medium">
                            {formatCurrency(app.totalPaidAmount ?? null, app.amount_currency)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Payments Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
                        {/* <p className="text-sm text-gray-600">
                            Current status:{' '}
                            <span className="font-semibold text-gray-900">
                                {app.payment_status ?? 'pending'}
                            </span>
                        </p> */}
                    </div>
                </div>

                {paymentProgress && (
                    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Application payment progress</p>
                                <p className="text-xs text-gray-600">
                                    {paymentProgress.paidInstallments}/{paymentProgress.totalInstallments} installments •{' '}
                                    {paymentProgress.nextDue ? `Next due ${formatDate(paymentProgress.nextDue)}` : 'No upcoming due date'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-semibold text-[#7b2a40]">{paymentProgress.percent}%</p>
                                <p className="text-xs text-gray-600">
                                    Paid {formatCurrency(paymentProgress.paid ?? null, paymentProgress.currency)} of{' '}
                                    {formatCurrency(paymentProgress.total ?? null, paymentProgress.currency)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-3 h-2 w-full rounded-full bg-white">
                            <div
                                className="h-full rounded-full bg-[#7b2a40] transition-all"
                                style={{ width: `${paymentProgress.percent}%` }}
                            />
                        </div>
                    </div>
                )}

                {payments.length === 0 ? (
                    <p className="text-sm text-gray-600">No payment proof uploaded yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-gray-500 border-b">
                                <tr>
                                    <th className="px-4 py-2 text-left font-semibold">Reference</th>
                                    <th className="px-4 py-2 text-left font-semibold">Amount</th>
                                    <th className="px-4 py-2 text-left font-semibold">Uploaded</th>
                                    <th className="px-4 py-2 text-left font-semibold">Verified</th>
                                    <th className="px-4 py-2 text-left font-semibold">Status</th>
                                    <th className="px-4 py-2 text-left font-semibold">Note</th>
                                    <th className="px-4 py-2 text-left font-semibold">Receipt</th>
                                    <th className="px-4 py-2 text-right font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {payments.map((payment) => (
                                    <tr key={payment.id ?? payment.reference}>
                                        <td className="px-4 py-3 font-medium text-gray-900">{payment.reference ?? '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">{formatCurrency(payment.amount, payment.currency)}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatDate(payment.created_at)}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatDate(payment.verified_at)}</td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getPaymentStatusClass(payment.status)}`}
                                            >
                                                {payment.status ?? 'pending'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{payment.verification_note ?? '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {payment.receipt_url ? (
                                                <a
                                                    href={payment.receipt_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:underline"
                                                >
                                                    View receipt
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {payment.status === 'pending' && (
                                                <button
                                                    onClick={() => handleVerifyClick(payment)}
                                                    className="text-sm font-medium text-white px-3 py-1.5 rounded-md"
                                                    style={{ backgroundColor: primary }}
                                                >
                                                    Verify
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {paymentProgress?.installments?.length ? (
                    <div className="mt-4 space-y-2">
                        <h3 className="text-sm font-semibold text-gray-900">Installment breakdown</h3>
                        <div className="grid gap-3 md:grid-cols-2">
                            {paymentProgress.installments.map((installment) => (
                                <div key={installment.id ?? installment.installment_number} className="rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold">
                                            Installment {installment.installment_number ?? '—'} ({installment.percentage ?? 0}%)
                                        </span>
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                                                installment.is_paid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                            }`}
                                        >
                                            {installment.is_paid ? 'Paid' : 'Pending'}
                                        </span>
                                    </div>
                                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                                        <span>Amount: {formatCurrency(installment.amount ?? null, paymentProgress.currency)}</span>
                                        <span>Paid: {formatCurrency(installment.amount_paid ?? null, paymentProgress.currency)}</span>
                                        <span>Due: {formatDate(installment.due_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900">Submitted Documents</h2>
                <DocumentViewer
                    documents={app.documents}
                />
            </div>

            <ApplicationStatusModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                applicationId={app.id ?? ''}
                currentStatus={app.status ?? ''}
            />
            <ApplicationProgrammeModal
                isOpen={isProgrammeModalOpen}
                onClose={() => setProgrammeModalOpen(false)}
                applicationId={app.id ?? ''}
                currentProgrammeId={app.programme_id ?? ''}
                currentProgrammeCohortId={app.programme_cohort_id ?? ''}
            />
            <ApplicationPaymentVerificationModal
                isOpen={isVerifyModalOpen}
                onClose={handleCloseVerification}
                applicationId={app.id ?? ''}
                payment={selectedPayment}
            />
        </div>
    )
}

export default ApplicationViewPage
